import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import NoData from "../pages/main/NoData";
import Main from "../redux/actions/main.action";
import { BaseState, StateKeys } from "../redux/states/main.state";
import ApiEndpoint from "../services/api.endpoint.service";
import { InterestResponse } from "../services/api.response.service";
import { AppState } from "../services/app.service";
import Languages from "./Languages";
import Loader from "./Loader";

export interface InterestsModalProps {
  interests: BaseState<Array<InterestResponse>>;
  userInterest: BaseState<Array<InterestResponse>>;
  show: boolean;
  onHide: () => void;
  blocking?: boolean;
  onSubmitSuccess?: () => void;
}

const InterestsModal = ({
  interests,
  userInterest,
  show,
  onHide,
  blocking = false,
  onSubmitSuccess,
}: InterestsModalProps) => {
  const dispatch = useDispatch();

  const [currentInterest, setCurrentInterest] = useState<InterestResponse[]>(
    [],
  );
  const [updatedInterest, setUpdatedInterest] = useState<number[]>([]);
  const [allInterest, setAllInterest] = useState<InterestResponse[]>([]);
  const submitPendingRef = useRef(false);
  const onSubmitSuccessRef = useRef(onSubmitSuccess);
  onSubmitSuccessRef.current = onSubmitSuccess;
  const prevUserInterestPageStateRef = useRef(userInterest.pageState);

  useEffect(() => {
    setCurrentInterest(userInterest.data);

    const currentIds: number[] = [];
    if (userInterest?.data) {
      userInterest.data.forEach((interest: InterestResponse) => {
        currentIds.push(interest.id);
      });
    }

    const available: InterestResponse[] = [];
    interests.data.forEach((interest) => {
      if (!currentIds.includes(interest.id)) {
        available.push(interest);
      }
    });

    setAllInterest(available);
    setUpdatedInterest(currentIds);
  }, [userInterest.data, interests.data]);

  const submitUpdateInterest = () => {
    submitPendingRef.current = true;
    dispatch(
      Main.postPaginatedPageData({
        stateKey: StateKeys.USER_INTERESTS,
        apiEndpoint: ApiEndpoint.USER_INTERESTS,
        previousPage: 0,
        req: { interests: updatedInterest },
        clear: true,
      }),
    );
  };

  useEffect(() => {
    const prev = prevUserInterestPageStateRef.current;
    prevUserInterestPageStateRef.current = userInterest.pageState;

    if (!submitPendingRef.current) return;

    if (
      prev === AppState.LOADING &&
      (userInterest.pageState === AppState.ERROR ||
        userInterest.pageState === AppState.ERROR_404)
    ) {
      submitPendingRef.current = false;
      return;
    }

    if (prev === AppState.LOADING && userInterest.pageState === AppState.LOADED) {
      submitPendingRef.current = false;
      onSubmitSuccessRef.current?.();
    }
  }, [userInterest.pageState]);

  const removeInterest = (interest: InterestResponse) => {
    if (updatedInterest.includes(interest.id)) {
      setCurrentInterest(
        currentInterest.filter((item) => item.id !== interest.id),
      );
      setUpdatedInterest(updatedInterest.filter((id) => id !== interest.id));
      setAllInterest([...allInterest, interest]);
    }
  };

  const addInterest = (interest: InterestResponse) => {
    if (!updatedInterest.includes(interest.id)) {
      setCurrentInterest([...currentInterest, interest]);
      setAllInterest(allInterest.filter((item) => item.id !== interest.id));
      setUpdatedInterest([...updatedInterest, interest.id]);
    }
  };

  const handleModalHide = () => {
    if (!blocking) {
      onHide();
    }
  };

  /** Still waiting on first user-interests response */
  const userInterestsPending =
    userInterest.pageState === AppState.IDLE ||
    userInterest.pageState === AppState.LOADING ||
    userInterest.pageState === AppState.DATA_REFRESH;

  /** Still waiting on all-interests list (catalog) */
  const allInterestsPending =
    interests.pageState === AppState.IDLE ||
    interests.pageState === AppState.LOADING ||
    interests.pageState === AppState.LOADING_MORE ||
    interests.pageState === AppState.DATA_REFRESH;

  const mustSelectInterestBeforeClose =
    blocking && !userInterestsPending && updatedInterest.length === 0;

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      backdrop={blocking ? "static" : true}
      keyboard={!blocking}
      centered
      scrollable
      dialogClassName="interests-modal-dialog"
    >
      <Modal.Header closeButton={!mustSelectInterestBeforeClose}>
        <Modal.Title>
          <Languages langKey="updateInterest" />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="interests-modal__body">
        <section className="interests-modal__section">
          <h6 className="interests-modal__section-title mb-3">
            <Languages langKey="yourInterests" />
          </h6>
          {!userInterestsPending ? (
            <>
              {currentInterest.length > 0 ? (
                <ul className="interests-modal__chip-list list-unstyled mb-0">
                  {currentInterest.map((interest: InterestResponse) => (
                    <li key={interest.id}>
                      <span className="interests-modal__chip interests-modal__chip--selected">
                        <span className="interests-modal__chip-label">
                          {interest.name}
                        </span>
                        <button
                          type="button"
                          className="interests-modal__chip-action"
                          onClick={() => removeInterest(interest)}
                          aria-label="Remove interest"
                        >
                          <i className="ri-indeterminate-circle-fill" />
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="interests-modal__hint text-muted mb-0">
                  <Languages langKey="selectInterestsFromBelow" />
                </p>
              )}
            </>
          ) : (
            <Col md={12}>
              <Loader noCard imageLoader />
            </Col>
          )}
        </section>

        <section className="interests-modal__section interests-modal__section--spaced">
          <h6 className="interests-modal__section-title mb-3">
            <Languages langKey="allInterest" />
          </h6>
          {!allInterestsPending ? (
            <>
              {allInterest.length > 0 ? (
                <ul className="interests-modal__chip-list list-unstyled mb-0">
                  {allInterest.map((interest: InterestResponse) => (
                    <li key={interest.id}>
                      <span className="interests-modal__chip interests-modal__chip--available">
                        <span className="interests-modal__chip-label">
                          {interest.name}
                        </span>
                        <button
                          type="button"
                          className="interests-modal__chip-action"
                          onClick={() => addInterest(interest)}
                          aria-label="Add interest"
                        >
                          <i className="ri-add-circle-fill" />
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <NoData
                  title={
                    interests.data.length > 0
                      ? "noInterest"
                      : "selectedAllInterest"
                  }
                />
              )}
            </>
          ) : (
            <Col md={12}>
              <Loader noCard imageLoader />
            </Col>
          )}
        </section>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-primary"
          title="Submit"
          onClick={() => submitUpdateInterest()}
          disabled={
            userInterest.pageState === AppState.LOADING ||
            mustSelectInterestBeforeClose
          }
        >
          {userInterest.pageState === AppState.LOADING && (
            <Spinner animation="border" size="sm" className="me-1" />
          )}
          <Languages langKey="submit" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InterestsModal;
